import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon, ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';
import { FLOW_ENGINE_ENDPOINTS } from '../../config/flowEngine';

interface ExecutionLog {
  timestamp: string;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
  data?: any;
}

interface ExecutionTerminalProps {
  executionData?: any;
  isVisible: boolean;
  onToggle: () => void;
}

const ExecutionTerminal: React.FC<ExecutionTerminalProps> = ({ 
  executionData, 
  isVisible,
  onToggle 
}) => {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [height, setHeight] = useState(320); // 80 * 4 = 320px (h-80)
  const [isResizing, setIsResizing] = useState(false);
  const terminalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (executionData) {
      const newLog: ExecutionLog = {
        timestamp: new Date().toISOString(),
        level: executionData.success ? 'success' : 'error',
        message: executionData.message || 'Flow végrehajtva',
        data: executionData
      };
      
      setLogs(prev => [...prev, newLog]);
      
      // Ha van executionId, 3 másodperc után lekérdezzük az execution log-ot
      if (executionData.executionId && !executionData.result) {
        setTimeout(async () => {
          try {
            // Get auth token
            const { auth } = await import('../../lib/firebase');
            const user = auth.currentUser;
            const token = user ? await user.getIdToken() : null;
            
            const headers: Record<string, string> = {};
            if (token) {
              headers['Authorization'] = `Bearer ${token}`;
            }
            
            const response = await fetch(
              `${FLOW_ENGINE_ENDPOINTS.executions}/${executionData.executionId}/logs`,
              { headers }
            );
            if (response.ok) {
              const logsData = await response.json();
              
              // Hozzáadjuk a node eredményeket a terminálhoz
              if (logsData.logs && logsData.logs.length > 0) {
                logsData.logs.forEach((nodeLog: any) => {
                  const resultLog: ExecutionLog = {
                    timestamp: nodeLog.timestamp || new Date().toISOString(),
                    level: nodeLog.status === 'completed' ? 'success' : 'error',
                    message: `📦 ${nodeLog.node_type} (${nodeLog.node_id})`,
                    data: nodeLog.output_data
                  };
                  setLogs(prev => [...prev, resultLog]);
                });
                
                // Végső összefoglaló
                const summaryLog: ExecutionLog = {
                  timestamp: new Date().toISOString(),
                  level: 'success',
                  message: `✅ Flow befejezve - ${logsData.logs.length} node végrehajtva`,
                  data: { total_nodes: logsData.logs.length }
                };
                setLogs(prev => [...prev, summaryLog]);
              }
            } else {
              // console.error('Failed to fetch execution logs:', await response.text());
            }
          } catch (error) {
            // console.error('Error fetching execution logs:', error);
          }
        }, 3000);
      }
      
      // Auto scroll to bottom
      setTimeout(() => {
        if (terminalRef.current) {
          terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [executionData]);

  const clearLogs = () => {
    setLogs([]);
  };

  // Resize handling
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const newHeight = window.innerHeight - e.clientY;
      if (newHeight >= 48 && newHeight <= 600) { // Min 48px, max 600px
        setHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'success': return 'text-green-400';
      case 'error': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-blue-400';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'success': return '✓';
      case 'error': return '✗';
      case 'warning': return '⚠';
      default: return 'ℹ';
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 shadow-2xl transition-all duration-200 z-50"
      style={{ height: isExpanded ? `${height}px` : '48px' }}
    >
      {/* Resize Handle */}
      {isExpanded && (
        <div
          onMouseDown={handleMouseDown}
          className={`absolute top-0 left-0 right-0 h-1 cursor-ns-resize hover:bg-blue-500 transition-colors ${
            isResizing ? 'bg-blue-500' : 'bg-transparent'
          }`}
          title="Húzd a magasság állításához"
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-semibold text-gray-200">
              💻 Execution Terminal
            </span>
          </div>
          <span className="text-xs text-gray-400">
            {logs.length} log{logs.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="flex items-center space-x-2">
          <button
            onClick={clearLogs}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
            title="Clear logs"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4" />
            ) : (
              <ChevronUpIcon className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={onToggle}
            className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Terminal Content */}
      {isExpanded && (
        <div 
          ref={terminalRef}
          className="h-full overflow-y-auto p-4 font-mono text-sm bg-gray-900"
          style={{ maxHeight: 'calc(100% - 48px)' }}
        >
          {logs.length === 0 ? (
            <div className="text-gray-500 text-center py-8">
              <div className="text-4xl mb-2">💻</div>
              <div>Nincs még kimenet. Futtass egy flow-t!</div>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log, index) => (
                <div key={index} className="flex items-start space-x-3 group">
                  {/* Timestamp */}
                  <span className="text-gray-500 text-xs flex-shrink-0 w-24">
                    {new Date(log.timestamp).toLocaleTimeString('hu-HU')}
                  </span>

                  {/* Level Icon */}
                  <span className={`${getLevelColor(log.level)} flex-shrink-0 w-4`}>
                    {getLevelIcon(log.level)}
                  </span>

                  {/* Message */}
                  <div className="flex-1 min-w-0">
                    <div className={`${getLevelColor(log.level)} break-words`}>
                      {log.message}
                    </div>

                    {/* Data */}
                    {log.data && (
                      <details className="mt-1 group-hover:opacity-100 opacity-70 transition-opacity">
                        <summary className="text-gray-400 text-xs cursor-pointer hover:text-gray-300">
                          📊 Kimenet adatok
                        </summary>
                        <pre className="mt-2 p-3 bg-gray-800 rounded text-xs text-gray-300 overflow-x-auto border border-gray-700">
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExecutionTerminal;
